import { ApiError } from "@/lib/api";
import { useI18n, type I18nKey } from "@/lib/i18n";

/**
 * Translate any error thrown from a mutation/query into a localized
 * string suitable for inline form-level rendering ("Invalid email
 * or password", rate-limit toast, etc.).
 *
 * Lookup order:
 *   1. `ApiError.code` matches a known auth-error key — render that
 *      key's localized copy. For `auth.errors.rate_limited` we also
 *      interpolate `{seconds}` from `details.retryAfterSec`.
 *   2. Any other `ApiError` → fall back to `auth.errors.unknown`.
 *      We deliberately do NOT render the server's English `message`
 *      verbatim — that's how raw HTTP strings leak into a localized
 *      UI. The fallback keeps the experience consistent.
 *   3. Anything else (network error, thrown plain Error) → also
 *      `auth.errors.unknown`.
 *
 * Returns null when given null/undefined so callers can write
 * `useErrorMessage(mutation.error)` without an outer guard.
 */
export function useErrorMessage(): (err: unknown) => string | null {
  const { t } = useI18n();

  return (err: unknown): string | null => {
    if (!err) return null;

    if (err instanceof ApiError) {
      // Rate-limit special case — interpolate the retry-after seconds
      // so the user sees an actionable countdown instead of a generic
      // "too many requests".
      if (err.code === "auth.errors.rate_limited") {
        const seconds = typeof err.details.retryAfterSec === "number" ? err.details.retryAfterSec : 1;
        return t("auth.errors.rate_limited", { seconds });
      }

      // Any code we've localized, even ones that don't have a
      // bespoke branch — `useT()` resolves the key directly.
      if (err.code && isKnownErrorCode(err.code)) {
        return t(err.code as I18nKey);
      }

      // Unrecognised ApiError: generic copy. Logs `code` (or its
      // absence) so production telemetry can spot codes we've
      // missed translating.
      if (import.meta.env.DEV) {
        console.warn(`[error-message] Unrecognised ApiError code: ${err.code ?? "(none)"}`);
      }
      return t("auth.errors.unknown");
    }

    // Non-ApiError throw (TypeError, network, etc.) — same generic
    // fallback. We don't try to detect "offline" specifically; the
    // PWA install prompt + service worker handle that surface.
    return t("auth.errors.unknown");
  };
}

/**
 * Whitelist of error codes we have localized strings for. Kept here
 * (not in `auth-errors.ts`) because the canonical source of truth is
 * the dictionary itself — if someone adds a code without adding the
 * matching i18n entry, we'd rather render the generic fallback than
 * call `t()` on a missing key.
 */
const KNOWN_ERROR_CODES = new Set<string>([
  "auth.errors.invalid_credentials",
  "auth.errors.email_already_registered",
  "auth.errors.reset_link_invalid",
  "auth.errors.verify_link_invalid",
  "auth.errors.rate_limited",
  "auth.errors.unauthorized",
]);

function isKnownErrorCode(code: string): boolean {
  return KNOWN_ERROR_CODES.has(code);
}
